import { cn } from '@/lib/utils';
import type { Tag, TagColor } from '@littlebooker/dal';

/**
 * Palette token → Tailwind classes.
 *
 * Written out literally rather than built as `bg-${color}-badge`: Tailwind scans the
 * source for class names, so an interpolated class is purged from the bundle and the
 * badge renders unstyled. The theme variables behind these are already dark-mode aware.
 */
const TAG_COLOR_CLASSES: Record<TagColor, string> = {
    blue: 'bg-blue-badge text-blue-badge-foreground',
    orange: 'bg-orange-badge text-orange-badge-foreground',
    yellow: 'bg-yellow-badge text-yellow-badge-foreground',
    purple: 'bg-purple-badge text-purple-badge-foreground',
    green: 'bg-green-badge text-green-badge-foreground',
    red: 'bg-red-badge text-red-badge-foreground',
    amber: 'bg-amber-badge text-amber-badge-foreground',
};

export const tagColorClasses = (color: TagColor | string | null | undefined): string =>
    TAG_COLOR_CLASSES[(color as TagColor) ?? 'blue'] ?? TAG_COLOR_CLASSES.blue;

type TagBadgeProps = {
    tag: Pick<Tag, 'label' | 'color'> & { description?: string | null };
    className?: string;
    /** Renders an × that calls this. Omit for read-only display. */
    onRemove?: () => void;
};

export function TagBadge({ tag, className, onRemove }: TagBadgeProps) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                tagColorClasses(tag.color),
                className,
            )}
            title={tag.description || undefined}
        >
            {tag.label}
            {onRemove && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="opacity-60 hover:opacity-100"
                    aria-label={`Remove ${tag.label}`}
                >
                    ×
                </button>
            )}
        </span>
    );
}
